/**
 * Jubit one-time SSO exchange token → Supabase session.
 *
 * The redirect half of the unified-auth story, paired with the popup half in
 * `jubitSsoBridge.ts`. jubit.ai is the source of truth for identity, profile and
 * tier; its `auth-jubit-sso` edge function mints a single-use `exchange_token` and
 * sends the user to `<product>/auth/sso-callback?token=…`. The path comes from the
 * cross-product registry (`supabase/functions/_shared/product-config.ts`), which
 * registers `jubuddy` and `gghere` with `ssoCallbackPath: '/auth/sso-callback'`.
 *
 * Until this module landed, jubuddy.com and gghere.com had no such route: the
 * function happily minted a token and pointed the browser at a 404, burning the
 * one-time token and leaving the user signed out with no way to retry.
 *
 * jubuddy's own `VITE_SUPABASE_URL` already IS the Jubit project
 * (`uqtkxmlpoenphznnapsv` — see `auth/supabase.ts`), so redemption goes to the same
 * project that issued the token; no second client and no cross-project trust.
 *
 * `validate_token` needs no Authorization header (the token IS the credential), and
 * is deliberately single-use — never call it twice for one token.
 */

/** Supabase session pair returned by a successful redemption. */
export interface JubitSsoSession {
  accessToken: string;
  refreshToken: string;
}

export type JubitSsoTokenErrorCode =
  | 'not-configured'
  | 'no-token'
  | 'invalid-token'
  | 'needs-reauth'
  | 'network';

export interface JubitSsoTokenFailure {
  ok: false;
  code: JubitSsoTokenErrorCode;
  /** Message safe to show a user. */
  message: string;
}

export type JubitSsoTokenResult = { ok: true; session: JubitSsoSession } | JubitSsoTokenFailure;

interface RedeemDeps {
  /** Defaults to the two Supabase keys, read off `import.meta.env` by name;
   *  injectable so tests never touch real env. */
  env?: Record<string, string | undefined>;
  fetchImpl?: typeof fetch;
}

/**
 * Pure: is this string plausibly a JWT / opaque refresh token? Mirrors the shape
 * checks in `jubitSsoBridge.ts` — a truncated or `undefined`-stringified token must
 * never reach `setSession`, where it would corrupt stored auth state.
 */
function isPlausibleToken(value: unknown, minLength: number): value is string {
  return typeof value === 'string' && value.length >= minLength && value !== 'undefined';
}

/**
 * Redeem a one-time exchange token for a Supabase session.
 *
 * Never throws — every failure is a typed result so the route can render a real
 * message instead of a blank screen. The caller is responsible for calling this at
 * most once per token.
 */
export async function redeemJubitSsoToken(
  token: string | null,
  deps: RedeemDeps = {},
): Promise<JubitSsoTokenResult> {
  // Name each key: Vite inlines a bare `import.meta.env` as the WHOLE env object —
  // every VITE_* var, Vercel's commit metadata included — into the bundle.
  const env = deps.env ?? {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
    VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
  };
  const fetchImpl = deps.fetchImpl ?? globalThis.fetch;

  if (!token) {
    return { ok: false, code: 'no-token', message: 'This sign-in link is missing its token.' };
  }

  const baseUrl = env.VITE_SUPABASE_URL;
  const anonKey = env.VITE_SUPABASE_ANON_KEY;
  if (!baseUrl || !anonKey || typeof fetchImpl !== 'function') {
    return {
      ok: false,
      code: 'not-configured',
      message: 'Sign-in is not configured for this environment.',
    };
  }

  let payload: Record<string, unknown>;
  try {
    const response = await fetchImpl(`${baseUrl}/functions/v1/auth-jubit-sso`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: anonKey },
      body: JSON.stringify({ action: 'validate_token', exchange_token: token }),
    });
    payload = (await response.json()) as Record<string, unknown>;
  } catch {
    return {
      ok: false,
      code: 'network',
      message: 'Could not reach the sign-in service. Check your connection and try again.',
    };
  }

  if (!payload || payload.success !== true) {
    // The function returns one combined message for expired / already-used /
    // unknown, so we do not pretend to tell those apart.
    return {
      ok: false,
      code: 'invalid-token',
      message: 'This sign-in link has expired or was already used. Please sign in again.',
    };
  }

  // `needs_reauth` means the token was valid but no session could be minted —
  // treat it as a sign-in prompt, not a hard error.
  if (payload.needs_reauth === true) {
    return { ok: false, code: 'needs-reauth', message: 'Please sign in again to continue.' };
  }

  const session = payload.session as { access_token?: unknown; refresh_token?: unknown } | undefined;
  if (!isPlausibleToken(session?.access_token, 20) || !isPlausibleToken(session?.refresh_token, 8)) {
    return {
      ok: false,
      code: 'needs-reauth',
      message: 'Your session could not be created. Please sign in again.',
    };
  }

  return {
    ok: true,
    session: { accessToken: session.access_token, refreshToken: session.refresh_token },
  };
}

/** Pure: same-origin path guard shared with `/auth/callback`, so `?next=` can never
 *  be turned into an open redirect to another host. */
export function safeSsoNext(raw: string | null, fallback = '/'): string {
  return raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : fallback;
}
